// client/src/pages/OrdersPage.jsx
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useOrders, useOrderStats, useAutoRefresh, useAudit, useWebhookEvents, useSync30d, useImportStatus, useValidateStatus } from "../hooks/useOrders";
import {
  triggerOrderImport,
  triggerOrderValidation,
  triggerRevalidateAll,
  triggerRevalidateLineStatuses,
  importOrdersByNumbers,
  fetchRetentionBackups,
  downloadRetentionBackup,
  fetchOrderStatuses,
} from "../services/api";
import OrderTable from "../components/OrderTable";
import OrderDetail from "../components/OrderDetail";
import StatsCards from "../components/StatsCards";
import FiltersBar from "../components/FiltersBar";
import MismatchAlert from "../components/MismatchAlert";
import AuditPanel from "../components/AuditPanel";
import WebhookEventLog from "../components/WebhookEventLog";
import { formatEasternDateTime } from "../utils/timezone";

function parseOrderNumbersCsv(raw) {
  const lines = String(raw || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const parsed = [];
  const seen = new Set();

  for (let i = 0; i < lines.length; i++) {
    const cols = lines[i]
      .split(/[,\t;]+/)
      .map((c) => c.trim().replace(/^"|"$/g, ""))
      .filter(Boolean);

    for (const col of cols) {
      const lower = col.toLowerCase();
      if (
        i === 0 &&
        (lower === "order_number" ||
          lower === "order number" ||
          lower === "ordernumber" ||
          lower === "order")
      ) {
        continue;
      }
      if (!seen.has(col)) {
        seen.add(col);
        parsed.push(col);
      }
    }
  }

  return parsed;
}

function chunkArray(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableManualImportError(err) {
  const status = Number(err?.status || 0);
  const message = String(err?.message || "").toLowerCase();
  if ([0, 408, 425, 429, 500, 502, 503, 504].includes(status)) return true;
  return (
    message.includes("network error") ||
    message.includes("timeout") ||
    message.includes("failed to fetch") ||
    message.includes("too many requests")
  );
}

const MANUAL_IMPORT_CHUNK_SIZE = 25;
const MANUAL_IMPORT_RETRY_LIMIT = 3;

const OrdersPage = () => {
  const [filters, setFilters] = useState({
    location: "",
    shiphero_fulfillment_status: "",
    pending_only: "",
    search: "",
    date_from: "",
    date_to: "",
    history_action: "",
    history_note: "",
  });
  const [cardFilter, setCardFilter] = useState(""); // filter from stats card click
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [revalidatingLineStatuses, setRevalidatingLineStatuses] = useState(false);
  const [revalidateLineStatusesNote, setRevalidateLineStatusesNote] = useState("");
  const [manualImportBusy, setManualImportBusy] = useState(false);
  const [manualImportError, setManualImportError] = useState("");
  const [manualImportResult, setManualImportResult] = useState(null);
  const [manualImportProgress, setManualImportProgress] = useState(null);
  const [manualOrderNumbers, setManualOrderNumbers] = useState([]);
  const [manualCsvName, setManualCsvName] = useState("");
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupsError, setBackupsError] = useState("");
  const [sort, setSort] = useState({ key: "order_date", dir: "desc" });

  // Debounced search — sends search query to server after 400ms of no typing
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [filters.search]);

  // Debounced ShipHero-history note filter (same pattern as search)
  const [debouncedHistoryNote, setDebouncedHistoryNote] = useState("");
  const historyNoteTimerRef = useRef(null);
  useEffect(() => {
    if (historyNoteTimerRef.current) clearTimeout(historyNoteTimerRef.current);
    historyNoteTimerRef.current = setTimeout(() => {
      setDebouncedHistoryNote(filters.history_note);
    }, 400);
    return () => clearTimeout(historyNoteTimerRef.current);
  }, [filters.history_note]);

  // Build API filters (search is server-side for cross-page results)
  const apiFilters = useMemo(() => {
    const f = { status: "any", limit: 25, sort: sort.key, dir: sort.dir };
    if (filters.location) f.location = filters.location;
    if (filters.shiphero_fulfillment_status) f.shiphero_fulfillment_status = filters.shiphero_fulfillment_status;
    if (filters.pending_only) f.pending_only = filters.pending_only;
    if (debouncedSearch) f.search = debouncedSearch;
    if (cardFilter) f.filter = cardFilter; // stats card filter (should_cancel, mismatch, etc.)
    if (filters.date_from) f.date_from = filters.date_from;
    if (filters.date_to) f.date_to = filters.date_to;
    if (filters.history_action) f.history_action = filters.history_action;
    if (debouncedHistoryNote) f.history_note = debouncedHistoryNote;
    return f;
  }, [filters.location, filters.shiphero_fulfillment_status, filters.pending_only, debouncedSearch, sort.key, sort.dir, cardFilter, filters.date_from, filters.date_to, filters.history_action, debouncedHistoryNote]);

  const handleSort = useCallback((key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  }, []);

  const { orders, loading, error, pagination, lastFetch, refresh, nextPage, prevPage } = useOrders(apiFilters);
  // Cards respond to the same window the table shows: date + location + search
  const statsFilters = useMemo(() => ({
    date_from: filters.date_from,
    date_to: filters.date_to,
    location: filters.location,
    search: debouncedSearch,
  }), [filters.date_from, filters.date_to, filters.location, debouncedSearch]);
  const { stats } = useOrderStats(statsFilters);

  // Real fulfillment statuses present in the current location/date window —
  // feeds the dropdown nested with the Location filter
  const [statusOptions, setStatusOptions] = useState([]);
  useEffect(() => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.location) params.location = filters.location;
    fetchOrderStatuses(params)
      .then((data) => setStatusOptions(data.statuses || []))
      .catch(() => setStatusOptions([]));
  }, [filters.date_from, filters.date_to, filters.location]);

  // Layer 2 protection — audit & webhook feed
  const { summary: auditSummary, results: auditResults, mismatches, running: auditRunning, runAudit } = useAudit();
  const { events: webhookEvents, loading: eventsLoading } = useWebhookEvents(10);

  // 30-day sync
  const {
    status: sync30dStatus,
    starting: sync30dStarting,
    error: sync30dError,
    startSync: startSync30d,
  } = useSync30d();

  // Import + validate status panels
  const { status: importStatus, startPolling: startImportPolling } = useImportStatus();
  const { status: validateStatus, startPolling: startValidatePolling } = useValidateStatus();
  const sync30dLogs = useMemo(() => {
    return Array.isArray(sync30dStatus?.logs) ? sync30dStatus.logs.slice(-5).reverse() : [];
  }, [sync30dStatus?.logs]);
  const sync30dProgressText = useMemo(() => {
    if (!sync30dStatus) return "";
    if (sync30dStatus.phase === "fetching") {
      const completed = Number(sync30dStatus.windowsCompleted || 0);
      const total = Number(sync30dStatus.windowsTotal || 0);
      const windowLabel = sync30dStatus.currentWindowKey
        ? `Current day: ${sync30dStatus.currentWindowKey}`
        : "Waiting for next day window";
      return `${windowLabel}. Completed ${completed}/${total} daily windows, scanned ${sync30dStatus.pages || 0} pages, imported ${sync30dStatus.fetched || 0} orders.`;
    }
    if (sync30dStatus.phase === "validating") {
      return `Validating ${sync30dStatus.validated || 0}/${sync30dStatus.totalToValidate || 0} synced orders against Shopify.`;
    }
    if (sync30dStatus.phase === "complete") {
      return `Last run imported ${sync30dStatus.fetched || 0} orders across ${sync30dStatus.pages || 0} pages and validated ${sync30dStatus.validated || 0}.`;
    }
    if (sync30dStatus.phase === "paused") {
      return sync30dStatus.error ||
        `The sync paused at a checkpoint after scanning ${sync30dStatus.pages || 0} pages. Click the button again to continue.`;
    }
    if (sync30dStatus.phase === "error") {
      return sync30dStatus.error || "The last 30-day sync stopped before completion.";
    }
    return "";
  }, [sync30dStatus]);

  // Protection panel visibility
  const [showProtection, setShowProtection] = useState(true);

  const handleImport = useCallback(async () => {
    if (!window.confirm(
      "Sync from ShipHero\n\nThis will pull all orders from ShipHero (last 30 days) and upsert them into the database.\n\nProceed?"
    )) return;
    setImporting(true);
    try {
      await triggerOrderImport(false);
      startImportPolling();
      setTimeout(() => refresh(), 3000);
    } catch (err) {
      console.error("Import failed:", err);
    } finally {
      setImporting(false);
    }
  }, [refresh, startImportPolling]);

  const handleValidate = useCallback(async () => {
    if (!window.confirm(
      "Validate with Shopify\n\nThis will validate all unvalidated orders against Shopify (checks location, line-item status, etc.).\n\nProceed?"
    )) return;
    setValidating(true);
    try {
      await triggerOrderValidation();
      startValidatePolling();
      setTimeout(() => refresh(), 3000);
    } catch (err) {
      console.error("Validation failed:", err);
    } finally {
      setValidating(false);
    }
  }, [refresh, startValidatePolling]);

  const handleRevalidateAll = useCallback(async () => {
    if (!window.confirm(
      "Re-validate All\n\nThis will reset ALL order validations and re-validate every order against Shopify from scratch. This may take a while.\n\nProceed?"
    )) return;
    setRevalidating(true);
    try {
      await triggerRevalidateAll();
      startValidatePolling();
      setTimeout(() => refresh(), 5000);
    } catch (err) {
      console.error("Revalidate-all failed:", err);
    } finally {
      setRevalidating(false);
    }
  }, [refresh, startValidatePolling]);

  const handleRevalidateLineStatuses = useCallback(async () => {
    if (!window.confirm(
      "Revalidate Line Statuses\n\nThis will re-derive the per-line-item status (Shopify item changed / location disabled) for every order in the database.\n\nNo external API calls are made — this is a fast, local-only operation.\n\nProceed?"
    )) return;
    setRevalidatingLineStatuses(true);
    setRevalidateLineStatusesNote("Re-deriving line-item statuses for all orders...");
    try {
      await triggerRevalidateLineStatuses();
      setRevalidateLineStatusesNote(
        "Started in background. Refreshing in a few seconds..."
      );
      setTimeout(() => refresh(), 4000);
      setTimeout(() => setRevalidateLineStatusesNote(""), 10000);
    } catch (err) {
      console.error("Revalidate line statuses failed:", err);
      setRevalidateLineStatusesNote(
        err?.response?.data?.error || err.message || "Revalidate failed"
      );
    } finally {
      setRevalidatingLineStatuses(false);
    }
  }, [refresh]);

  const handleSync30d = useCallback(async () => {
    if (!window.confirm(
      "Import Last 30 Days\n\nThis will fetch all orders from the last 30 days from ShipHero (daily windows) and validate them against Shopify. This is a long-running operation.\n\nProceed?"
    )) return;
    startSync30d();
  }, [startSync30d]);

  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    setBackupsError("");
    try {
      const data = await fetchRetentionBackups();
      setBackups(data.backups || []);
    } catch (err) {
      setBackupsError(err.message || "Failed to load retention backups");
    } finally {
      setBackupsLoading(false);
    }
  }, []);

  const handleBackupDownload = useCallback(async (filename) => {
    try {
      await downloadRetentionBackup(filename);
    } catch (err) {
      setBackupsError(err.message || "Download failed");
    }
  }, []);

  const handleDownloadManualImportTemplate = useCallback(() => {
    const csv = [
      "order_number",
      "028923564",
      "028948656",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "order-number-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleManualCsvUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const raw = await file.text();
      const parsed = parseOrderNumbersCsv(raw);
      setManualCsvName(file.name);
      setManualOrderNumbers(parsed);
      setManualImportResult(null);
      setManualImportProgress(null);
      if (parsed.length === 0) {
        setManualImportError("No order numbers found in the CSV file.");
      } else {
        setManualImportError("");
      }
    } catch (err) {
      setManualImportError(err.message || "Failed to read CSV file.");
      setManualOrderNumbers([]);
      setManualCsvName("");
    }
  }, []);

  const handleManualImport = useCallback(async () => {
    if (manualOrderNumbers.length === 0) {
      setManualImportError("Upload a CSV file with order numbers first.");
      return;
    }
    setManualImportBusy(true);
    setManualImportError("");
    setManualImportResult(null);
    const chunks = chunkArray(manualOrderNumbers, MANUAL_IMPORT_CHUNK_SIZE);
    setManualImportProgress({ current: 1, total: chunks.length });

    try {
      const aggregate = {
        requested: 0,
        processed: 0,
        imported: 0,
        skippedExisting: 0,
        validated: 0,
        notFound: 0,
        failed: 0,
        validationQueued: false,
      };

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setManualImportProgress({ current: i + 1, total: chunks.length });
        let result = null;

        for (let attempt = 1; attempt <= MANUAL_IMPORT_RETRY_LIMIT; attempt++) {
          try {
            result = await importOrdersByNumbers(chunk, false);
            break;
          } catch (err) {
            const shouldRetry =
              attempt < MANUAL_IMPORT_RETRY_LIMIT &&
              isRetryableManualImportError(err);

            if (!shouldRetry) {
              throw err;
            }

            setManualImportError(
              `Chunk ${i + 1}/${chunks.length} failed (${err.message || "request error"}). ` +
              `Retrying ${attempt}/${MANUAL_IMPORT_RETRY_LIMIT - 1}...`
            );
            await sleep(1000 * attempt);
          }
        }

        aggregate.requested += result.requested || chunk.length;
        aggregate.processed += result.processed || chunk.length;
        aggregate.imported += result.imported || 0;
        aggregate.skippedExisting += result.skippedExisting || 0;
        aggregate.validated += result.validated || 0;
        aggregate.notFound += result.notFound || 0;
        aggregate.failed += result.failed || 0;
        setManualImportError("");
        setManualImportResult({ ...aggregate });

        if (i < chunks.length - 1) {
          await sleep(100);
        }
      }

      try {
        await triggerOrderValidation();
        aggregate.validationQueued = true;
      } catch (validationErr) {
        if (validationErr?.status !== 409) {
          console.warn("[ManualImport] Background validation trigger failed:", validationErr.message);
        }
      }

      setManualImportResult({ ...aggregate });
      setTimeout(() => refresh(), 2000);
    } catch (err) {
      setManualImportError(err.message || "Manual import failed");
    } finally {
      setManualImportBusy(false);
      setManualImportProgress(null);
    }
  }, [manualOrderNumbers, refresh]);

  // Auto-refresh
  useAutoRefresh(refresh, 60, autoRefresh);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  // Refresh table when 30-day sync completes
  const prevSync30dPhase = useRef(null);
  useEffect(() => {
    if (prevSync30dPhase.current === "validating" && sync30dStatus?.phase === "complete") {
      refresh();
    }
    prevSync30dPhase.current = sync30dStatus?.phase || null;
  }, [sync30dStatus?.phase, refresh]);

  // Orders are already filtered server-side
  const filteredOrders = orders;

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key !== "search") setSelectedOrderId(null);
  };

  return (
    <div>
      {/* Error Banner */}
      {error && (
        <div style={{
          background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 12,
          padding: "14px 20px", marginBottom: 24, display: "flex",
          alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: "#b91c1c", fontSize: 14 }}>Failed to load orders</div>
            <div style={{ color: "#dc2626", fontSize: 13, marginTop: 2 }}>{error}</div>
          </div>
          <button onClick={refresh} style={{
            marginLeft: "auto", padding: "8px 16px",
            background: "#b91c1c", color: "white", border: "none",
            borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13,
          }}>
            Retry
          </button>
        </div>
      )}

      {/* Mismatch Alert — shown when audit finds location mismatches */}
      <MismatchAlert
        mismatches={mismatches}
        onRunAudit={runAudit}
        auditRunning={auditRunning}
      />

      {/* Stats — clickable cards filter the order table */}
      <StatsCards stats={stats} auditSummary={auditSummary} activeFilter={cardFilter} onFilterByCard={setCardFilter} />

      {/* Filters */}
      <FiltersBar
        filters={filters}
        onFilterChange={handleFilterChange}
        totalCount={filteredOrders.length}
        loading={loading}
        onRefresh={refresh}
        lastFetch={lastFetch}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={setAutoRefresh}
        cardFilter={cardFilter}
        onClearCardFilter={() => setCardFilter("")}
        statusOptions={statusOptions}
        exportParams={{
          filter: cardFilter || undefined,
          search: filters.search || undefined,
          sort: sort.key,
          dir: sort.dir,
          shiphero_fulfillment_status: filters.shiphero_fulfillment_status || undefined,
          pending_only: filters.pending_only || undefined,
          location: filters.location || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          history_action: filters.history_action || undefined,
          history_note: filters.history_note || undefined,
        }}
      />

      {/* Orders Table */}
      <OrderTable
        orders={filteredOrders}
        loading={loading}
        sort={sort}
        onSort={handleSort}
        onSelectOrder={(order) => setSelectedOrderId(order.id)}
        selectedId={selectedOrderId}
      />

      {/* Pagination + Import Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, flexWrap: "wrap", gap: 12 }}>
        {/* Import / Validate buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleImport}
            disabled={importing}
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #6366f1", background: importing ? "#e0e7ff" : "#6366f1",
              color: importing ? "#6366f1" : "white", cursor: importing ? "wait" : "pointer",
            }}
          >
            {importing ? "Importing..." : "Sync from ShipHero"}
          </button>
          <button
            onClick={handleValidate}
            disabled={validating}
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #10b981", background: validating ? "#d1fae5" : "#10b981",
              color: validating ? "#10b981" : "white", cursor: validating ? "wait" : "pointer",
            }}
          >
            {validating ? "Validating..." : "Validate with Shopify"}
          </button>
          <button
            onClick={handleSync30d}
            disabled={sync30dStarting || sync30dStatus?.running}
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #f59e0b",
              background: (sync30dStarting || sync30dStatus?.running) ? "#fef3c7" : "#f59e0b",
              color: (sync30dStarting || sync30dStatus?.running) ? "#f59e0b" : "white",
              cursor: (sync30dStarting || sync30dStatus?.running) ? "wait" : "pointer",
            }}
          >
            {sync30dStatus?.running
              ? sync30dStatus.phase === "fetching"
                ? `Syncing... ${sync30dStatus.windowsCompleted || 0}/${sync30dStatus.windowsTotal || 0} days`
                : sync30dStatus.phase === "validating"
                  ? `Validating... ${sync30dStatus.validated}/${sync30dStatus.totalToValidate}`
                  : "Syncing..."
              : sync30dStatus?.phase === "paused"
                ? "Resume Last 30 Days"
                : "Import Last 30 Days"}
          </button>
          <button
            onClick={handleRevalidateAll}
            disabled={revalidating}
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #8b5cf6",
              background: revalidating ? "#ede9fe" : "#8b5cf6",
              color: revalidating ? "#8b5cf6" : "white",
              cursor: revalidating ? "wait" : "pointer",
            }}
          >
            {revalidating ? "Re-validating..." : "Re-validate All"}
          </button>
          <button
            onClick={handleRevalidateLineStatuses}
            disabled={revalidatingLineStatuses}
            title="Re-derive per-line-item status (Shopify item changed / location disabled) for every order in the DB. Cheap/local — no external API calls."
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #0ea5e9",
              background: revalidatingLineStatuses ? "#e0f2fe" : "#0ea5e9",
              color: revalidatingLineStatuses ? "#0ea5e9" : "white",
              cursor: revalidatingLineStatuses ? "wait" : "pointer",
            }}
          >
            {revalidatingLineStatuses ? "Revalidating statuses..." : "Revalidate Line Statuses"}
          </button>
        </div>
        {revalidateLineStatusesNote ? (
          <div style={{ marginTop: 8, fontSize: 12, color: "#075985" }}>
            {revalidateLineStatusesNote}
          </div>
        ) : null}

        {/* Page navigation */}
        {pagination.pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={prevPage}
              disabled={pagination.page <= 1}
              style={{
                padding: "10px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                border: "1.5px solid #e2e8f0", background: "white",
                cursor: pagination.page > 1 ? "pointer" : "not-allowed",
                color: pagination.page > 1 ? "#334155" : "#cbd5e1",
              }}
            >
              ← Previous
            </button>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>
              Page {pagination.page} of {pagination.pages}
              {pagination.total ? ` (${pagination.total} orders)` : ""}
            </span>
            <button
              onClick={nextPage}
              disabled={pagination.page >= pagination.pages}
              style={{
                padding: "10px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                border: "1.5px solid #e2e8f0", background: "white",
                cursor: pagination.page < pagination.pages ? "pointer" : "not-allowed",
                color: pagination.page < pagination.pages ? "#334155" : "#cbd5e1",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {(sync30dStatus?.phase && sync30dStatus.phase !== "idle") || sync30dError ? (
        <div style={{
          marginTop: 14,
          border: "1px solid #fed7aa",
          borderRadius: 12,
          background: "#fff7ed",
          padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#9a3412" }}>
                Last 30 Days Sync
              </div>
              <div style={{ fontSize: 12, color: "#9a3412", marginTop: 4 }}>
                {sync30dProgressText || sync30dError}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#7c2d12", textAlign: "right" }}>
              <div>Phase: {sync30dStatus?.phase || "unknown"}</div>
              {sync30dStatus?.startedAt && (
                <div>Started: {formatEasternDateTime(sync30dStatus.startedAt)}</div>
              )}
              {sync30dStatus?.completedAt && (
                <div>Completed: {formatEasternDateTime(sync30dStatus.completedAt)}</div>
              )}
              {sync30dStatus?.resumed ? <div>Mode: resumed from checkpoint</div> : null}
            </div>
          </div>

          {sync30dStatus?.currentWindowKey ? (
            <div style={{ fontSize: 12, color: "#7c2d12", marginBottom: 8 }}>
              Working on {sync30dStatus.currentWindowKey}
              {sync30dStatus.currentWindowPages ? `, ${sync30dStatus.currentWindowPages} pages scanned in this window` : ""}
              {sync30dStatus.currentWindowFetched ? `, ${sync30dStatus.currentWindowFetched} orders imported in this window` : ""}
            </div>
          ) : null}

          {sync30dLogs.length > 0 ? (
            <div style={{
              borderRadius: 10,
              background: "#fffbeb",
              border: "1px solid #fde68a",
              padding: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                Recent Sync Log
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {sync30dLogs.map((entry, index) => (
                  <div key={`${entry.time || "log"}-${index}`} style={{ fontSize: 12, color: "#78350f" }}>
                    {entry.time ? `${formatEasternDateTime(entry.time)}: ` : ""}{entry.message}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Sync from ShipHero status panel */}
      {importStatus && importStatus.phase !== "idle" ? (
        <div style={{
          marginTop: 14, border: "1px solid #c7d2fe", borderRadius: 12,
          background: "#eef2ff", padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#3730a3" }}>Sync from ShipHero</div>
              <div style={{ fontSize: 12, color: "#3730a3", marginTop: 4 }}>
                {importStatus.phase === "running"
                  ? `Importing... ${importStatus.fetched || 0} orders across ${importStatus.pages || 0} pages${importStatus.currentStatus ? ` (status: ${importStatus.currentStatus})` : ""}`
                  : importStatus.phase === "complete"
                    ? `Last run imported ${importStatus.fetched || 0} orders across ${importStatus.pages || 0} pages`
                    : importStatus.phase === "error"
                      ? `Error: ${importStatus.error}`
                      : ""}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#312e81", textAlign: "right" }}>
              <div>Phase: {importStatus.phase}</div>
              {importStatus.startedAt && <div>Started: {formatEasternDateTime(importStatus.startedAt)}</div>}
              {importStatus.completedAt && <div>Completed: {formatEasternDateTime(importStatus.completedAt)}</div>}
            </div>
          </div>
          {importStatus.logs?.length > 0 && (
            <div style={{ borderRadius: 10, background: "#e0e7ff", border: "1px solid #a5b4fc", padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#3730a3", marginBottom: 6 }}>Recent Log</div>
              <div style={{ display: "grid", gap: 4 }}>
                {[...importStatus.logs].reverse().slice(0, 5).map((entry, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#3730a3" }}>
                    {entry.time ? `${formatEasternDateTime(entry.time)}: ` : ""}{entry.message}
                  </div>
                ))}
              </div>
            </div>
          )}
          {importStatus.countCheck && (() => {
            const cc = importStatus.countCheck;
            const mismatches = (cc.mismatches || []).filter(m => m.delta > 0);
            const hasBackfill = importStatus.backfillTriggered || cc.backfillUpserted > 0;
            const allGood = mismatches.length === 0;
            return (
              <div style={{
                marginTop: 10, borderRadius: 10, padding: 10,
                background: allGood ? "#d1fae5" : "#fef3c7",
                border: `1px solid ${allGood ? "#6ee7b7" : "#fcd34d"}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: allGood ? "#065f46" : "#92400e", marginBottom: 4 }}>
                  Daily Count Check {allGood ? "✓ All matched" : `⚠ ${mismatches.length} day(s) mismatched`}
                  {hasBackfill && cc.backfillUpserted > 0 && (
                    <span style={{ marginLeft: 8, fontWeight: 400 }}>
                      (auto-backfilled {cc.backfillUpserted} order{cc.backfillUpserted !== 1 ? "s" : ""})
                    </span>
                  )}
                </div>
                {mismatches.length > 0 && (
                  <div style={{ display: "grid", gap: 2 }}>
                    {mismatches.map((m) => (
                      <div key={m.day} style={{ fontSize: 11, color: "#92400e" }}>
                        {m.day}: ShipHero={m.shipheroCount} DB={m.dbCount} (missing {m.delta})
                      </div>
                    ))}
                  </div>
                )}
                {cc.nullDateCount > 0 && (
                  <div style={{ fontSize: 11, color: "#78350f", marginTop: 2 }}>
                    {cc.nullDateCount} order(s) with no order date (webhook timing)
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ) : null}

      {/* Validate with Shopify / Re-validate All status panel */}
      {validateStatus && validateStatus.phase !== "idle" ? (
        <div style={{
          marginTop: 14, border: "1px solid #a7f3d0", borderRadius: 12,
          background: "#ecfdf5", padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#065f46" }}>
                {validateStatus.source === "revalidate-all" ? "Re-validate All" : "Validate with Shopify"}
              </div>
              <div style={{ fontSize: 12, color: "#065f46", marginTop: 4 }}>
                {validateStatus.phase === "running"
                  ? `Validating ${validateStatus.processed || 0} / ${validateStatus.total || 0} orders against Shopify (${validateStatus.succeeded || 0} ok, ${validateStatus.failed || 0} failed)`
                  : validateStatus.phase === "complete"
                    ? `Last run: ${validateStatus.processed || 0} validated, ${validateStatus.succeeded || 0} ok, ${validateStatus.failed || 0} failed`
                    : validateStatus.phase === "error"
                      ? `Error: ${validateStatus.error}`
                      : ""}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#064e3b", textAlign: "right" }}>
              <div>Phase: {validateStatus.phase}</div>
              {validateStatus.startedAt && <div>Started: {formatEasternDateTime(validateStatus.startedAt)}</div>}
              {validateStatus.completedAt && <div>Completed: {formatEasternDateTime(validateStatus.completedAt)}</div>}
            </div>
          </div>
          {/* Progress bar */}
          {validateStatus.phase === "running" && validateStatus.total > 0 && (
            <div style={{ marginBottom: 8, background: "#d1fae5", borderRadius: 8, height: 8, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 8, background: "#10b981",
                width: `${Math.min(100, Math.round((validateStatus.processed / validateStatus.total) * 100))}%`,
                transition: "width 0.4s ease",
              }} />
            </div>
          )}
          {validateStatus.logs?.length > 0 && (
            <div style={{ borderRadius: 10, background: "#d1fae5", border: "1px solid #6ee7b7", padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#065f46", marginBottom: 6 }}>Recent Log</div>
              <div style={{ display: "grid", gap: 4 }}>
                {[...validateStatus.logs].reverse().slice(0, 5).map((entry, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#065f46" }}>
                    {entry.time ? `${formatEasternDateTime(entry.time)}: ` : ""}{entry.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Manual CSV Import + Retention Backups */}
      <div style={{
        marginTop: 14,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
        gap: 12,
      }}>
        <div style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#ffffff",
          padding: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Manual Import by Order Numbers CSV
            </div>
            <button
              onClick={handleDownloadManualImportTemplate}
              style={{
                marginLeft: "auto",
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #bae6fd",
                background: "#f0f9ff",
                color: "#0369a1",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Download Template
            </button>
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleManualCsvUpload}
            style={{ marginBottom: 8, width: "100%" }}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            {manualCsvName
              ? `${manualCsvName} — ${manualOrderNumbers.length} order numbers parsed`
              : "Upload a CSV with one order number per row (or first column)."}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
            Accepted format: header `order_number`, then one order number per row.
          </div>
          {manualImportBusy && manualImportProgress && (
            <div style={{ fontSize: 12, color: "#0369a1", marginBottom: 8 }}>
              Importing chunk {manualImportProgress.current} of {manualImportProgress.total}
              {" "}({MANUAL_IMPORT_CHUNK_SIZE} order numbers max per request)
            </div>
          )}
          {manualImportError && (
            <div style={{
              marginBottom: 8,
              fontSize: 12,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: "6px 8px",
            }}>
              {manualImportError}
            </div>
          )}
          {manualImportResult && (
            <div style={{
              marginBottom: 8,
              fontSize: 12,
              color: "#065f46",
              background: "#ecfdf5",
              border: "1px solid #a7f3d0",
              borderRadius: 8,
              padding: "6px 8px",
            }}>
              Requested: {manualImportResult.requested}, Imported: {manualImportResult.imported}, Already in DB: {manualImportResult.skippedExisting || 0}, Validated: {manualImportResult.validated}, Not Found: {manualImportResult.notFound}, Failed: {manualImportResult.failed}
              {manualImportResult.validationQueued ? ", Background validation started" : ""}
            </div>
          )}
          <button
            onClick={handleManualImport}
            disabled={manualImportBusy || manualOrderNumbers.length === 0}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1.5px solid #0ea5e9",
              background: (manualImportBusy || manualOrderNumbers.length === 0) ? "#e0f2fe" : "#0ea5e9",
              color: (manualImportBusy || manualOrderNumbers.length === 0) ? "#0369a1" : "#ffffff",
              fontSize: 13,
              fontWeight: 700,
              cursor: (manualImportBusy || manualOrderNumbers.length === 0) ? "not-allowed" : "pointer",
            }}
          >
            {manualImportBusy
              ? manualImportProgress
                ? `Importing ${manualImportProgress.current}/${manualImportProgress.total}...`
                : "Importing..."
              : "Import Order Numbers"}
          </button>
        </div>

        <div style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#ffffff",
          padding: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Retention CSV Backups
            </div>
            <button
              onClick={loadBackups}
              disabled={backupsLoading}
              style={{
                marginLeft: "auto",
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                fontSize: 12,
                fontWeight: 600,
                cursor: backupsLoading ? "wait" : "pointer",
              }}
            >
              {backupsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          {backupsError && (
            <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 8 }}>{backupsError}</div>
          )}
          {backups.length === 0 ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>No backup CSV files available.</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {backups.map((b) => (
                <div key={b.name} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid #f3f4f6",
                  borderRadius: 8,
                  padding: "7px 8px",
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {b.name}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {(b.sizeBytes / 1024).toFixed(1)} KB • {formatEasternDateTime(b.modifiedAt)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleBackupDownload(b.name)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 7,
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#0f172a",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Layer 2 Protection Panel toggle */}
      <div style={{ marginTop: 32, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: "-0.02em" }}>
          🛡️ Two-Layer Protection
        </h2>
        <button
          onClick={() => setShowProtection((v) => !v)}
          style={{
            padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: "1.5px solid #e2e8f0", background: "white",
            color: "#64748b", cursor: "pointer",
          }}
        >
          {showProtection ? "Hide" : "Show"}
        </button>
      </div>

      {showProtection && (
        <>
          {/* Audit Panel */}
          <AuditPanel
            summary={auditSummary}
            results={auditResults}
            auditRunning={auditRunning}
            onRunAudit={runAudit}
          />

          {/* Webhook Event Log */}
          <WebhookEventLog events={webhookEvents} loading={eventsLoading} />
        </>
      )}

      {/* Order Detail Panel */}
      {selectedOrderId && (
        <OrderDetail orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
      )}
    </div>
  );
};

export default OrdersPage;
